/**
 * ============================================================================
 * [P2-01-B] Firestore Converters
 *
 * Firestore 문서와 애플리케이션 타입 간의 변환 함수
 *
 * 기능:
 * - textToFirestore: SavedText → Firestore 문서 데이터
 * - textFromFirestore: Firestore 스냅샷 → SavedText
 *
 * Safety:
 * - 모든 필드에 기본값(fallback) 제공
 * - Optional Chaining으로 null 안전성 확보
 * - Timestamp → Date 변환 처리
 * ============================================================================
 */

import { serverTimestamp, type DocumentSnapshot, type Timestamp } from 'firebase/firestore'
import {
  LEGACY_REFERENCE_ANALYSIS_PANEL_SIZE,
  REFERENCE_ANALYSIS_PANEL_SIZE,
  type ReferenceAnalysisPanelSize,
  type ReferenceAnalysisSummary,
  type ReferenceAnalysisScoreKey,
  type SavedText,
} from '@/types'

// ============================================================================
// Firestore 문서 데이터 타입 정의 (SavedText와 동기화)
// ============================================================================
export interface FirestoreTextData {
  content: string
  title: string
  type: 'edit' | 'reference' | 'script'
  topic?: string
  source?: string
  snsPlatforms?: string[]
  referenceType?: 'structure' | 'idea' | 'unspecified'
  userId: string
  charLimit?: number
  charCount: number
  isFavorite?: boolean  // [P3-01] 즐겨찾기 상태
  isUsed?: boolean      // 레퍼런스 수동 사용 표시
  linkedReferences?: string[]  // 참고 레퍼런스 ID 목록
  analysisSummary?: ReferenceAnalysisSummary
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>
  updatedAt: ReturnType<typeof serverTimestamp>
}

const ANALYSIS_SCORE_KEYS: ReferenceAnalysisScoreKey[] = [
  'structure',
  'topic',
  'hook',
  'emotion',
  'utility',
  'trust',
  'cta',
]

const SUPPORTED_ANALYSIS_PANEL_SIZES: ReferenceAnalysisPanelSize[] = [
  LEGACY_REFERENCE_ANALYSIS_PANEL_SIZE,
  REFERENCE_ANALYSIS_PANEL_SIZE,
]

function isAnalysisSummary(value: unknown): value is ReferenceAnalysisSummary {
  if (!value || typeof value !== 'object') return false

  const summary = value as Partial<ReferenceAnalysisSummary>
  return (
    summary.promptVersion === 'reference-analysis-v1' &&
    SUPPORTED_ANALYSIS_PANEL_SIZES.includes(
      summary.simulatedPanelSize as ReferenceAnalysisPanelSize
    ) &&
    typeof summary.topScoreKey === 'string' &&
    ANALYSIS_SCORE_KEYS.includes(summary.topScoreKey as ReferenceAnalysisScoreKey) &&
    typeof summary.topScore === 'number' &&
    Array.isArray(summary.topFactors) &&
    Array.isArray(summary.extractedTopics) &&
    typeof summary.createdAt === 'string'
  )
}

// ============================================================================
// [방어] 중첩 undefined 제거 sanitizer.
// - plain object / array만 재귀(중첩 undefined strip).
// - Date / Timestamp / FieldValue(serverTimestamp 센티넬) / 원시값은 그대로 통과
//   (firebase 센티넬을 파괴하면 createdAt/updatedAt 영속이 깨짐).
// - analysisSummary 같은 중첩 객체에만 적용한다.
// ============================================================================
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

function sanitizeForFirestore(value: unknown): unknown {
  if (Array.isArray(value)) {
    // Firestore 배열은 undefined 원소를 허용하지 않음 → 제거 후 재귀.
    return value
      .filter((item) => item !== undefined)
      .map((item) => sanitizeForFirestore(item))
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value)) {
      if (nested === undefined) continue
      out[key] = sanitizeForFirestore(nested)
    }
    return out
  }

  // Date / Timestamp / FieldValue 센티넬 / 원시값은 비파괴 통과.
  return value
}

// ============================================================================
// [P2-01-B] SavedText → Firestore 문서 데이터 변환
// ============================================================================
export function textToFirestore(
  text: Omit<SavedText, 'id' | 'createdAt' | 'updatedAt'>
): Omit<FirestoreTextData, 'createdAt' | 'updatedAt'> & {
  createdAt: ReturnType<typeof serverTimestamp>
  updatedAt: ReturnType<typeof serverTimestamp>
} {
  // undefined 값을 제거하기 위해 기본값 사용
  const result: Record<string, unknown> = {
    content: text.content ?? '',
    title: text.title ?? '',
    type: text.type ?? 'edit',
    topic: text.topic ?? '',
    source: text.source ?? '',
    snsPlatforms: text.snsPlatforms ?? [],
    referenceType: text.referenceType ?? 'unspecified',
    userId: text.userId ?? '',
    charCount: text.charCount ?? text.content?.length ?? 0,
    isFavorite: text.isFavorite ?? false,  // [P3-01] 즐겨찾기 상태
    isUsed: text.isUsed ?? false,          // 레퍼런스 수동 사용 표시
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  // charLimit은 값이 있을 때만 추가 (undefined 방지)
  if (text.charLimit !== undefined) {
    result.charLimit = text.charLimit
  }

  // linkedReferences는 값이 있을 때만 추가
  if (text.linkedReferences && text.linkedReferences.length > 0) {
    result.linkedReferences = text.linkedReferences
  }

  if (text.analysisSummary) {
    if (isAnalysisSummary(text.analysisSummary)) {
      result.analysisSummary = text.analysisSummary
    } else if (import.meta.env.DEV) {
      // 조용한 드롭 대신 개발 중 가시화(compact 마커). 본 기능은 막지 않는다.
      console.warn('[textToFirestore] analysisSummary 형식 불일치 → 영속 제외')
    }
  }

  return result as Omit<FirestoreTextData, 'createdAt' | 'updatedAt'> & {
    createdAt: ReturnType<typeof serverTimestamp>
    updatedAt: ReturnType<typeof serverTimestamp>
  }
}

// ============================================================================
// [P2-01-B] Firestore 스냅샷 → SavedText 변환
// ============================================================================
export function textFromFirestore(snapshot: DocumentSnapshot): SavedText {
  const data = snapshot.data()

  // Safety: 문서 데이터가 없는 경우 에러 throw
  if (!data) {
    throw new Error(`[textFromFirestore] Document data is null for id: ${snapshot.id}`)
  }

  // Timestamp → Date 변환 헬퍼
  const toDate = (timestamp: Timestamp | null | undefined): Date => {
    if (!timestamp) return new Date()
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }
    return new Date()
  }

  return {
    id: snapshot.id,
    content: data.content ?? '',
    title: data.title ?? '',
    type: data.type ?? 'edit',
    topic: data.topic ?? '',
    source: data.source ?? '',
    snsPlatforms: Array.isArray(data.snsPlatforms) ? data.snsPlatforms : [],
    referenceType: data.referenceType ?? 'unspecified',
    userId: data.userId ?? '',
    charLimit: data.charLimit,
    charCount: data.charCount ?? data.content?.length ?? 0,
    isFavorite: data.isFavorite ?? false,  // [P3-01] 즐겨찾기 상태
    isUsed: data.isUsed ?? false,          // 레퍼런스 수동 사용 표시
    isDeleted: data.isDeleted ?? false,    // [P5-02] 휴지통 - 소프트 삭제
    deletedAt: data.deletedAt ?? undefined, // [P5-02] 휴지통 - 삭제 시간
    linkedReferences: Array.isArray(data.linkedReferences) ? data.linkedReferences : [],
    analysisSummary: isAnalysisSummary(data.analysisSummary)
      ? data.analysisSummary
      : undefined,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  }
}

// ============================================================================
// [P2-01-B] 부분 업데이트용 변환 함수
// - id, createdAt 필드 제거 (immutable 필드)
// ============================================================================
export function textUpdatesToFirestore(
  updates: Partial<SavedText>
): Partial<Omit<FirestoreTextData, 'createdAt'>> {
  // id, createdAt, updatedAt 필드 제거 (immutable 필드)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...safeUpdates } = updates

  // charCount 자동 계산 (content가 업데이트되는 경우)
  const result: Partial<Omit<FirestoreTextData, 'createdAt'>> = {
    updatedAt: serverTimestamp(),
  }

  // undefined 값 제거 - Firestore는 undefined를 허용하지 않음
  Object.entries(safeUpdates).forEach(([key, value]) => {
    // 최상위 undefined 스킵 보존: useTexts가 deletedAt: undefined를 의도적으로 전달한다.
    if (value === undefined) return

    // analysisSummary만 별도 분기: create(textToFirestore)와 대칭으로
    // isAnalysisSummary 통과분만 sanitize 후 기록하고, 실패 시 DEV warn 드롭.
    if (key === 'analysisSummary') {
      if (isAnalysisSummary(value)) {
        ;(result as Record<string, unknown>).analysisSummary =
          sanitizeForFirestore(value)
      } else if (import.meta.env.DEV) {
        console.warn('[textUpdatesToFirestore] analysisSummary 형식 불일치 → 영속 제외')
      }
      return
    }

    ;(result as Record<string, unknown>)[key] = value
  })

  // content가 있으면 charCount도 업데이트
  if (safeUpdates.content !== undefined) {
    result.charCount = safeUpdates.content.length
  }

  return result
}
